"use client";

import { defaultLocale } from "@/shared/lib/i18n/config";
import type { ReactNode } from "react";
import { createContext, use, useContext, useEffect, useState } from "react";
import { translations } from "./translations";

export type Language = "en" | "nl";

type Props = {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
}

const LanguageContext = createContext<Props | undefined>(undefined);

interface IProps {
    children: ReactNode;
    langPromise?: Promise<{ lang: string }>;
}

export const LanguageProvider = ({ children, langPromise }: IProps) => {
    // If we have a lang promise (from [lang] routes), use it
    // Otherwise fallback to client-side detection (legacy/compat)
    const params = langPromise ? use(langPromise) : null;

    // Initialize from params if available, otherwise default
    const initialLang = (params?.lang === "nl" || params?.lang === "en" ? params.lang : defaultLocale) as Language
    const [language, setLanguage] = useState<Language>(initialLang);

    useEffect(() => {
        if (params?.lang === "en" || params?.lang === "nl") {
            setLanguage(params.lang);
        }
    }, [params]);

    // This is kept for backward compatibility but operations should now use the switcher
    const handleSetLanguage = (lang: Language) => {
        setLanguage(lang);
    };

    const t = (key: string): string => {
        return translations[language][key as keyof typeof translations.en] || key;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }
    return context;
};
